import { useEffect, useMemo, useRef, useState } from 'react';
import { ItemImageEditorDialog } from '@/components/item/ItemImageEditorDialog';

interface Props {
  open: boolean;
  files: File[];
  onClose: () => void;
  onApply: (next: File[]) => void;
}

/**
 * Walks the caller through crop + rotate + flip for each staged file, one at a
 * time, using the existing `ItemImageEditorDialog`. Skipped files pass through
 * unchanged. Returns the ordered array via `onApply` when the queue drains.
 *
 * Bulk-first, no-frills queue — matches the app's "select them all, then bulk
 * do that" pattern for photo prep.
 */
export function BulkPhotoEditorSheet({ open, files, onClose, onApply }: Props) {
  const [index, setIndex] = useState(0);
  const editedRef = useRef<File[]>([]);
  const dataUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  useEffect(() => {
    if (open) {
      setIndex(0);
      editedRef.current = files.slice();
    }
  }, [open, files]);

  useEffect(() => {
    return () => {
      for (const u of dataUrls) URL.revokeObjectURL(u);
    };
  }, [dataUrls]);

  if (!open || files.length === 0) return null;

  const total = files.length;
  const current = files[index];
  const currentUrl = dataUrls[index];

  const advance = () => {
    if (index + 1 >= total) {
      onApply(editedRef.current);
    } else {
      setIndex((i) => i + 1);
    }
  };

  const handleSave = (blob: Blob) => {
    const original = current;
    const baseName = original.name.replace(/\.[^/.]+$/, '');
    const edited = new File([blob], `${baseName}.jpg`, {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now(),
    });
    editedRef.current[index] = edited;
    advance();
  };

  return (
    <>
      <ItemImageEditorDialog
        open={open}
        imageSrc={currentUrl}
        onClose={() => {
          // Treat close as "skip this one" so bulk flows don't stall — the
          // final Cancel is exposed only via the explicit onClose flow when the
          // user opts out entirely.
          advance();
        }}
        onSave={handleSave}
      />
      {/* Progress chip painted above the editor dialog */}
      <div
        className="pointer-events-none fixed left-1/2 -translate-x-1/2 z-[70] rounded-full bg-black/70 backdrop-blur px-3 py-1 text-[11px] font-medium tracking-wide text-white/90 border border-white/10"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        Crop &amp; rotate · {index + 1} of {total}
      </div>
    </>
  );
}
